import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Pressable, StyleSheet } from 'react-native';
import { View } from 'react-native';
import { BodyText, Icon, theme, usePressableState } from '@sg/garnish';

import { type IngredientModificationType } from '@order/utils';

import { CUSTOMIZATIONS_TABS } from '../../../../../constants';
import { type useFilteredIngredientsModifications } from '../../../../../hooks';
import type { ProductIngredientModifications } from '../../../../../state';
import { getSelectedQuantityByKind } from '../../../helpers';

// ─────────────────────────────────────────────────────────────────────────────

export const AddModificationCta = (props: AddModificationCtaProps) => {
  const {
    active,
    ingredientsModifications,
    ingredientModificationType,
    setActiveTabItemId,
    setDefaultActiveTargetId,
  } = props;

  // ─── Label ─────────────────────────────────────────────────────────────────

  const { formatMessage } = useIntl();
  const label = formatMessage(messages[ingredientModificationType]);

  // ─── Interactive ───────────────────────────────────────────────────────────

  const ref = useRef(null);
  const { isInteracting } = usePressableState(ref);

  const handleOnPress = useCallback(() => {
    setDefaultActiveTargetId(ingredientModificationType);
    setActiveTabItemId(CUSTOMIZATIONS_TABS.ADD_INGREDIENTS);
  }, [
    ingredientModificationType,
    setActiveTabItemId,
    setDefaultActiveTargetId,
  ]);

  // ─── Count Verification ────────────────────────────────────────────────────

  const selectedCount = useMemo(
    () =>
      getSelectedQuantityByKind({
        active,
        ingredientKindName: ingredientModificationType,
      }),
    [active, ingredientModificationType],
  );

  const maxCount =
    ingredientsModifications[ingredientModificationType]
      ?.maxAggregateQuantity || 0;

  if (selectedCount >= maxCount) return null;

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Pressable
      ref={ref}
      accessibilityRole="link"
      accessibilityLabel={label}
      style={styles.container}
      onPress={handleOnPress}
    >
      <View style={styles.icon}>
        <Icon width={16} height={16} name="IconPlus2" />
      </View>

      <BodyText
        underline={isInteracting}
        style={styles.label}
        sizeMatch={['18']}
      >
        {label}
      </BodyText>
    </Pressable>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  bases: {
    defaultMessage: 'Add base',
    description: 'Customization | Base details | Add base button label',
  },
  bread: {
    defaultMessage: 'Add bread',
    description: 'Customization | Dressing details | Add dressing button label',
  },
  toppings: {
    defaultMessage: 'Add topping',
    description: 'Customization | Topping details | Add topping button label',
  },
  premiums: {
    defaultMessage: 'Add premium',
    description: 'Customization | Premium details | Add premium button label',
  },
  dressings: {
    defaultMessage: 'Add dressing',
    description: 'Customization | Dressing details | Add dressing button label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing['1'],
    paddingHorizontal: theme.spacing['2'],
    gap: theme.spacing['2'],
  },
  icon: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    width: 30,
    height: 30,
    borderRadius: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    color: theme.colors.KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type FilteredIngredientsResponse = ReturnType<
  typeof useFilteredIngredientsModifications
>;

type AddModificationCtaProps = {
  active: ProductIngredientModifications['active'];
  ingredientsModifications: FilteredIngredientsResponse['ingredientsModifications'];
  ingredientModificationType: IngredientModificationType;
  setActiveTabItemId: Dispatch<SetStateAction<string>>;
  setDefaultActiveTargetId: (section: IngredientModificationType) => void;
};
